import React, { useEffect, useRef, useState } from "react";
import AOS from "aos";
import TextTransition, { presets } from "react-text-transition";

import BannerImg from "../../../../assets/images/bannerimg.jpg";
import Delivery1 from "../../../../assets/images/Sundarban-Cargo-D1.jpeg";
import Delivery3 from "../../../../assets/images/web-Banner-1.jpg.jpeg";
import MobileBanner2 from "../../../../assets/images/web-banner-2-mobile.png";
import MobileBanner3 from "../../../../assets/images/web-banner-3-mobile.jpg";

import GetLocalStorageUserId from "../../../sharedComponents/GetLocalStorageuserId";
import UserTokenHandler from "../../../sharedComponents/UserTokenHandler";
import { redirectToIdentityLogin } from "../../../../utils/identityLogin";
import { isMangoFeatureEnabled } from "../../../../utils/featureFlags";

const IMAGE_DELAY = 5000;

const scrollToShippingPrice = () => {
  const target =
    document.getElementById("get-shipping-price") ||
    document.querySelector(".quotation-section");
  target?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Banner = () => {
  const mangoFeatureEnabled = isMangoFeatureEnabled();
  const TEXTS = ["purpose", "performance", "service"];

  const [textIndex, setTextIndex] = useState(0);
  const [slideIndex, setSlideIndex] = useState(0);
  const [userId, setUserId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  const timerRef = useRef(null);
  const videoRef = useRef(null);

  UserTokenHandler();

  const desktopSlides = [
    { type: "image", src: BannerImg, alt: "Sundarban Cargo banner", showHeroText: true },
    {
      type: "image",
      src: Delivery1,
      alt: "Sundarban Cargo delivery",
      scrollToPricing: true,
    },
    {
      type: "image",
      src: Delivery3,
      alt: "Sundarban Cargo services",
      scrollToPricing: true,
    },
  ];

  const mobileSlides = [
    {
      type: "image",
      src: MobileBanner2,
      alt: "Fresh Mango Export",
      scrollToPricing: true,
    },
    {
      type: "image",
      src: MobileBanner3,
      alt: "Mango delivery from Bangladesh to UK",
      scrollToPricing: true,
    },
  ];

  const slides = mangoFeatureEnabled
    ? isMobile
      ? mobileSlides
      : desktopSlides
    : [desktopSlides[0]];
  const activeSlide = slides[slideIndex];
  const showHeroText = Boolean(activeSlide?.showHeroText);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setSlideIndex(0);
  }, [isMobile]);

  useEffect(() => {
    const interval = setInterval(() => setTextIndex((i) => i + 1), 3000);
    return () => clearInterval(interval);
  }, []);

  const startAutoplay = () => {
    clearTimeout(timerRef.current);
    const currentSlide = slides[slideIndex];
    if (currentSlide.type === "video") return;
    timerRef.current = setTimeout(
      () => setSlideIndex((i) => (i + 1) % slides.length),
      IMAGE_DELAY,
    );
  };

  useEffect(() => {
    const currentSlide = slides[slideIndex];
    if (currentSlide.type === "video" && videoRef.current) {
      clearTimeout(timerRef.current);
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    } else {
      startAutoplay();
    }
    return () => clearTimeout(timerRef.current);
  }, [slideIndex, isMobile]);

  const handleVideoEnd = () => setSlideIndex((i) => (i + 1) % slides.length);

  useEffect(() => {
    AOS.init({ easing: "ease-in-sine" });
    setUserId(GetLocalStorageUserId());
  }, []);

  const handleShipWithUsClick = () => {
    localStorage.removeItem("sundorbonPricingDraft");
    if (userId) window.location.href = "/bookings";
    else redirectToIdentityLogin(window.location.origin);
  };

  const handlePricingSlideClick = (slide) => {
    if (!slide.scrollToPricing) return;
    scrollToShippingPrice();
  };

  return (
    <div
      className="hero relative overflow-hidden pt-2 lg:pt-0"
      style={{
        minHeight: isMobile ? "100vh" : "800px",
        paddingBottom: isMobile ? "5rem" : "0",
      }}
    >
      {slides.map((slide, index) => (
        <div
          key={`${isMobile ? "m" : "d"}-${index}`}
          className={`absolute inset-0 transition-all duration-[3000ms] ease-in-out
            ${
              index === slideIndex
                ? "opacity-100 scale-100 blur-0 z-10"
                : "opacity-0 scale-105 blur-sm z-0"
            }`}
        >
          {slide.type === "image" ? (
            slide.scrollToPricing ? (
              <button
                type="button"
                onClick={() => handlePricingSlideClick(slide)}
                className="block w-full h-full p-0 border-0 bg-transparent cursor-pointer"
                aria-label="Get shipping price"
              >
                <img
                  src={slide.src}
                  alt={slide.alt || "Banner slide"}
                  className="w-full h-full object-center banner-img pointer-events-none"
                  style={{ objectFit: isMobile ? "cover" : "contain" }}
                  loading={index === 0 ? "eager" : "lazy"}
                />
              </button>
            ) : (
              <img
                src={slide.src}
                alt={slide.alt || "Banner slide"}
                className="w-full h-full object-center banner-img"
                style={{ objectFit: isMobile ? "cover" : "contain" }}
                loading={index === 0 ? "eager" : "lazy"}
              />
            )
          ) : (
            <video
              ref={videoRef}
              className="w-full h-full"
              src={slide.src}
              muted
              playsInline
              onEnded={handleVideoEnd}
              style={{ maxHeight: isMobile ? "130vh" : "100%" }}
            />
          )}

          {slide.showHeroText && (
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent pointer-events-none" />
          )}
        </div>
      ))}

      {showHeroText && (
        <div className="absolute lg:left-36 left-5 text-left text-white z-20 pointer-events-none">
          <div className="lg:max-w-md max-w-sm pointer-events-auto" data-aos="fade-right">
            <h1
              className="mb-5 font-bold uppercase text-orange-500 mt-0 lg:mt-0"
              style={{ fontSize: isMobile ? "2rem" : "3.5rem" }}
            >
              <TextTransition direction="left" springConfig={presets.wobbly}>
                {TEXTS[textIndex % TEXTS.length]}
              </TextTransition>
            </h1>

            <i>
              <h1 className="mb-5 text-4xl font-bold">DRIVEN.</h1>
            </i>

            <p
              className={`mb-5 ${
                isMobile ? "text-sm" : "text-md"
              } leading-relaxed`}
            >
              Are you tired of unpredictability when it comes to shipping or
              hauling full truckload freight?
            </p>

            <p
              className={`mb-5 ${
                isMobile ? "text-sm" : "text-md"
              } leading-relaxed`}
            >
              Enter a world where modern technology meets high performance and
              deep industry insights. With Sundarban Cargo Service Uk Ltd you can
              turn logistical challenges into strategic opportunities, better
              decision-making, and more successful outcomes.
            </p>

            <button
              onClick={handleShipWithUsClick}
              className={`btn bg-orange-500 hover:bg-orange-300 hover:text-black border-none rounded-sm text-white ${
                isMobile ? "text-sm px-4 py-2" : "text-lg px-6 py-3"
              }`}
            >
              Ship With Us
            </button>
          </div>
        </div>
      )}

      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-3 z-30">
        {slides.map((_, index) => (
          <button
            key={index}
            type="button"
            onClick={() => setSlideIndex(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === slideIndex ? "w-10 bg-orange-500" : "w-5 bg-white/60"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default Banner;
